import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import isoWeek from "dayjs/plugin/isoWeek";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import { IObservableValue } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";
import { CvpQueryClientProvider } from "../api/queryClient";
import { useShouldShowDarkMode } from "../api/settings/useShouldShowDarkMode";
import { AdminProvider } from "../business-components/adminSettings/AdminContext";
import { Layout } from "../business-components/layout";
import { Routes } from "../business-components/Routes";
import { AppType } from "../constants/AppType";
import { UnsavedChangesHandler } from "../generic-components/common/FormikUnsavedChangesBlocker";
import { CvpToast } from "../generic-components/messages/CvpToastMessages";
import { getAppType } from "../getAppType";
import { TranslationProvider, useLocStore } from "../localization";
import { LocContext } from "../localization/LocContext";
import { DayjsLocaleProvider } from "../localization/useDayjsLocalized";
import { usePrimeLocale } from "../localization/usePrimeLocale";
import { CvpSettings } from "../settings/CvpSettings";
import { useSettings } from "../settings/useSettings";
import { ThemeProvider } from "../themes/generated";
import { useRecordProfileVisit } from "../user/useRecordProfileVisit";
import { NewlyLinkedIdpToast } from "../user/NewlyLinkedIdpToast";
import { useRestoreConcernAfterHardcodedLinkClick } from "../url/useRestoreConcernAfterHardcodedLinkClick";
import { SearchParamProvider } from "../url/useSearchParam";
import { PluginComponentRenderer } from "../utils/PluginComponents";
import { concatClassnames } from "../utils/util";
import { AppGlobalStyles, UserProvidedStyle } from "./AppGlobalStyles";
import { AppStyleProps, getThemeSettingValue } from "./getThemeSettingValue";
import { UndrawCssVariablesProvider } from "../assets/UndrawCssVariablesProvider";
import { PushNotificationsProvider } from "../push/PushNotificationsProvider";
dayjs.extend(isoWeek);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(duration);

export interface CVPortalProps {
  settings: IObservableValue<CvpSettings>;
}

export const App = ({ settings }: CVPortalProps) => {
  return (
    <React.Suspense fallback={null}>
      <AdminProvider>
        <CvpQueryClientProvider>
          <SearchParamProvider>
            <UnsavedChangesHandler>
              <DayjsLocaleProvider>
                <AppContainerInner />
                <TranslationProvider />
              </DayjsLocaleProvider>
            </UnsavedChangesHandler>
          </SearchParamProvider>
        </CvpQueryClientProvider>
      </AdminProvider>
    </React.Suspense>
  );
};

const AppContainerInner = observer(() => {
  const settings = useSettings();
  usePrimeLocale();

  // prefetch
  useLocStore(LocContext.GENERAL);

  useRestoreConcernAfterHardcodedLinkClick();
  useRecordProfileVisit();

  const theme = useShouldShowDarkMode().darkMode
    ? "lara-dark-purple"
    : "lara-light-purple";

  return (
    <ThemeProvider theme={theme} replaceColors>
      <AppGlobalStyles />
      <UndrawCssVariablesProvider />
      <UserProvidedStyle css={settings.css} />
      <PushNotificationsProvider />

      <AppStyles
        className={concatClassnames([
          "p-component",
          "cvp-app-container",
          getAppType() === AppType.iframe && "cvp-app-container-iframe",
          getAppType() === AppType.cvWebtempNo && "cvp-app-container-cvwebtempno",
        ])}
        fontSize={settings.fontSize}
      >
        <Layout>
          <React.Suspense fallback={null}>
            <CvpToast />
            <NewlyLinkedIdpToast />
            <PluginComponentRenderer />
            <Routes />
          </React.Suspense>
        </Layout>
      </AppStyles>
    </ThemeProvider>
  );
});

const AppStyles = styled.div<AppStyleProps>`
  &.cvp-app-container {
    color: var(--text-color);
    box-sizing: border-box;

    &.cvp-app-container-cvwebtempno {
      min-height: calc(100 * var(--vh));
    }

    &:not(.cvp-app-container-cvwebtempno) {
      /* width: calc(min(var(--standard-cvp-width), 100%) - 2rem);
      max-width: calc(min(var(--standard-cvp-width), 100%) - 2rem);
      margin-left: auto;
      margin-right: auto;
      margin-top: 0;
      margin-bottom: 0;
      padding-top: 1rem;
      padding-bottom: 1rem; */
    }

    background-color: var(--surface-ground);

    &.cvp-app-container-iframe {
      background-color: transparent;
    }

    ${(p) => getThemeSettingValue(p, "fontSize")};

    /* Undo special css in customer pages */
    mark {
      padding: 0;
      background: yellow;
    }
  }

  .hidden {
    display: none;
  }

  .p-link {
    color: var(--text-color);
  }
`;
